import type { HvyModule, HvyMount } from './hvy-types';
import { createNoticeBatchDocument } from './notice-document';

export async function createSurveyNoticesPdf(neighborhood: string, codes: string[], questionCount = 13): Promise<Blob> {
  const hvyModuleUrl = __HVY_EMBED_URL__;
  const [hvy, templateResponse] = await Promise.all([
    import(/* @vite-ignore */ hvyModuleUrl) as Promise<HvyModule>,
    fetch('/api/admin/survey-notice-template'),
  ]);
  if (!templateResponse.ok) throw new Error('The survey notice template could not be loaded.');

  const template = hvy.deserializeDocumentBytes(new Uint8Array(await templateResponse.arrayBuffer()), '.phvy');
  const batch = createNoticeBatchDocument(template, neighborhood, codes, questionCount);
  const renderRoot = document.createElement('div');
  renderRoot.className = 'notice-pdf-renderer';
  document.body.append(renderRoot);
  let mount: HvyMount | null = null;
  try {
    mount = hvy.mountHvyViewer({
      root: renderRoot,
      document: batch.document,
      plugins: [hvy.plugins.qrCode],
      controls: false,
      persistSessionState: false,
    });
    const pdf = await mount.getPdfBlob({
      strategy: {
        rules: batch.pageBreakSectionIds.map((id) => ({ id, pageBreakBefore: true })),
      },
    });
    if (pdf.size === 0) throw new Error('The survey notice PDF was empty.');
    return pdf;
  } finally {
    mount?.destroy();
    renderRoot.remove();
  }
}

export function downloadSurveyNoticesPdf(neighborhood: string, surveyKey: string, surveyLabel: string, pdf: Blob): void {
  const url = URL.createObjectURL(pdf);
  const link = document.createElement('a');
  link.href = url;
  const prefix = surveyKey === 'original' ? filenameSlug(neighborhood) : `${filenameSlug(surveyLabel)}-${filenameSlug(neighborhood)}`;
  link.download = `${prefix}-survey-notices.pdf`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function filenameSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'neighborhood';
}
